import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CircleNotch, LockSimple } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const inputCls = "w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none";
const inputStyle = { borderColor: "var(--bk-border)", color: "var(--bk-text)" };

export function SetBankBalanceDialog({ businessId, open, onOpenChange, onPosted }) {
  const [info, setInfo] = useState(null);
  const blank = () => ({ account_id: "", balance: "", effective_date: today(), note: "" });
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInfo(null);
    api.get(`/businesses/${businessId}/cash-balance`).then((r) => {
      setInfo(r.data);
      setForm((f) => ({ ...f, account_id: f.account_id && r.data.accounts?.some((a) => a.id === f.account_id) ? f.account_id : (r.data.accounts?.[0]?.id || ""), balance: "" }));
    }).catch(() => {});
  }, [open, businessId]);

  const account = info?.accounts?.find((a) => a.id === form.account_id);
  const entered = parseFloat(form.balance);
  const diff = account && !Number.isNaN(entered) ? Math.round((entered - account.balance) * 100) / 100 : null;
  const isFirst = !(info?.adjustments || []).some((a) => a.account_id === form.account_id);

  const submit = async () => {
    if (Number.isNaN(entered)) return toast.error("Enter the balance shown on your bank or cash account.");
    if (!form.note.trim()) return toast.error("Add a note (e.g. 'Per bank statement Sep 3').");
    setSaving(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/cash-balance/set`, { account_id: form.account_id, balance: entered, effective_date: form.effective_date, note: form.note });
      toast.success(`${data.adjustment.account_name} set to ${money(data.adjustment.entered_balance)} — posted ${data.adjustment.difference >= 0 ? "+" : ""}${money(data.adjustment.difference)} to ${data.adjustment.counter_account}`);
      onOpenChange(false);
      setForm(blank());
      onPosted?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't post that balance.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-bank-balance-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ color: "var(--bk-text)" }}>Set bank balance</DialogTitle>
          <DialogDescription>Enter what the account actually holds. Rowan posts only the difference as a dated journal entry, so the Balance Sheet, Home and this forecast all move together.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs" style={{ color: "var(--bk-text-2)" }}>Account
            <select value={form.account_id} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))} data-testid="cash-balance-account" className={`${inputCls} bg-white`} style={inputStyle}>
              {(info?.accounts || []).map((a) => <option key={a.id} value={a.id}>{`${a.name} — books show ${money(a.balance)}`}</option>)}
            </select>
          </label>
          <label className="block text-xs" style={{ color: "var(--bk-text-2)" }}>Actual balance ($)
            <input type="number" step="0.01" value={form.balance} onChange={(e) => setForm((f) => ({ ...f, balance: e.target.value }))} data-testid="cash-balance-amount" className={inputCls} style={inputStyle} placeholder="e.g. 8500.00" autoFocus />
          </label>
          <label className="block text-xs" style={{ color: "var(--bk-text-2)" }}>Effective date
            <DateInput value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} data-testid="cash-balance-date" className={inputCls} style={inputStyle} />
          </label>
          <label className="block text-xs" style={{ color: "var(--bk-text-2)" }}>Note (required)
            <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="cash-balance-note" className={inputCls} style={inputStyle} placeholder="Per bank statement, Sep 3" />
          </label>
          {diff !== null && (
            <p className="text-xs rounded-md px-3 py-2" style={{ background: "var(--bk-page)", color: "var(--bk-text)" }} data-testid="cash-balance-preview">
              {Math.abs(diff) < 0.005
                ? "Books already match — nothing to post."
                : <>Will post <strong>{diff > 0 ? "+" : ""}{money(diff)}</strong> to {account?.name} against <strong>{isFirst ? "Opening Balance Equity" : "Balance Adjustment"}</strong>{isFirst ? " (first balance for this account)" : " (correction — audit trail kept)"}.</>}
            </p>
          )}
          <button onClick={submit} disabled={saving || diff === null || Math.abs(diff) < 0.005} data-testid="cash-balance-submit" className="bk-btn-primary w-full py-2.5 text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {saving ? <CircleNotch size={16} className="animate-spin" /> : <LockSimple size={16} weight="bold" />} Post balance
          </button>
          {(info?.adjustments || []).length > 0 && (
            <div className="pt-2 border-t" style={{ borderColor: "var(--bk-border)" }} data-testid="cash-balance-history">
              <p className="text-xs font-semibold mb-1" style={{ color: "var(--bk-text-2)" }}>History</p>
              {info.adjustments.slice(0, 5).map((a) => (
                <p key={a.id} className="text-xs" style={{ color: "var(--bk-text-2)" }}>{a.effective_date} · {a.account_name} → {money(a.entered_balance)} ({a.difference >= 0 ? "+" : ""}{money(a.difference)} via {a.counter_account}) · {a.note}</p>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
